import React from 'react';
import {
  CopyCustomProcessRequest,
  CreatePlanFromCustomProcessRequest, Plan, TeamSetupArea,
  useCustomSetupAreasService,
  useTeamSetupAreasService
} from 'nekst-api';
import {
  AfterSubmitFunc,
  ButtonSize,
  FormTemplate,
  OptionSelectionButton,
  TextInput
} from 'ui-builder';
import { usePopupFormManager, ConfirmDeletePopup, ConfirmPositiveActionPopup, InformationMessage } from 'features/nekst-widgets';
import { useTeamSetupAreaContextRequired } from '../TeamSetupAreaContext';
import { UpdateCustomSetupAreaForm } from './CreateCustomSetupAreaForm';
import useSettingsNavigator from '../../settingsNavigator';
import { ProjectTypeDropdown } from 'features/tasks-forms';
import usePlansNavigator from '../../../plans/plansNavigator';

function CopyCustomProcessForm(props: {
  customProcessId: number,
  originalName: string,
  afterSubmitFunc?: AfterSubmitFunc,
}) {
  const customSetupAreasService = useCustomSetupAreasService();

  const settingsNavigator = useSettingsNavigator();

  return (
    <FormTemplate<CopyCustomProcessRequest, TeamSetupArea>
      title={`Copy "${props.originalName}"`}
      submitFormFunc={async (data) => {
        return await customSetupAreasService.copyCustomSetupArea(
          props.customProcessId,
          data,
        );
      }}
      validationSchema={customSetupAreasService.getValidationSchema()}
      initialData={{
        name: `${props.originalName} (Copy)`,
        alias: '',
        projectType: undefined,
      }}
      afterSubmitFunc={async (data) => {
        props.afterSubmitFunc?.(data);
        settingsNavigator.openCustomProcessDetailFieldsPage(data.alias);
      }}
    >
      <InformationMessage>
        A new process will be created with all detail boxes and workflows copied from the original process.
      </InformationMessage>
      <TextInput source="name" label="Process Name" />
      <TextInput
        source="alias"
        label="Alias"
        hint="Alias is used in Zapier and Public API integrations"
      />
      <ProjectTypeDropdown source="projectType" label="Type" isClearable={false} />
    </FormTemplate>
  );
}

function CreateWorkflowForm(props: {
  customProcessId: number,
  processName: string,
  afterSubmitFunc?: AfterSubmitFunc,
}) {
  const customSetupAreasService = useCustomSetupAreasService();

  const plansNavigator = usePlansNavigator();

  return (
    <FormTemplate<CreatePlanFromCustomProcessRequest, Plan>
      title={`Create Workflow from "${props.processName}"`}
      submitFormFunc={async (data) => {
        return await customSetupAreasService.createPlanFromCustomProcess(
          props.customProcessId,
          data,
        );
      }}
      afterSubmitFunc={async (data) => {
        props.afterSubmitFunc?.(data);
        plansNavigator.openPlanPage(data.id);
      }}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true,
            minLength: 3,
            maxLength: 100,
          },
        },
      }}
      initialData={{
        name: `${props.processName} Workflow`,
      }}
    >
      <TextInput source="name" label="Workflow Name" />
    </FormTemplate>
  );
}

export default function CustomProcessActionsButton() {
  const popupManager = usePopupFormManager();
  const { teamSetupArea } = useTeamSetupAreaContextRequired();
  const teamSetupAreasService = useTeamSetupAreasService();

  const settingsNavigator = useSettingsNavigator();

  const handleArchive = () => {
    popupManager.openForm(
      <ConfirmDeletePopup
        title="Archive Process"
        message={`Are you sure you want to archive "${teamSetupArea.name}"? This action can be undone later.`}
        deleteFunc={async () => {
          await teamSetupAreasService.archiveSetupArea(teamSetupArea.id);
          settingsNavigator.openMarketsPage();
        }}
        withConfirmationInput
      />
    );
  };

  const handleMakeDefault = () => {
    popupManager.openForm(
      <ConfirmPositiveActionPopup
        title="Set Default Process"
        message={`Make "${teamSetupArea.name}" the default process? This will affect new projects.`}
        buttonText="Set as Default"
        submitFormFunc={async () => {
          await teamSetupAreasService.setDefaultSetupArea(teamSetupArea.id);
        }}
      />
    );
  };

  const options = [
    {
      label: 'Update Process',
      value: 'update',
      onSelected: () => {
        popupManager.openForm(
          <UpdateCustomSetupAreaForm teamSetupArea={teamSetupArea} />,
        );
      },
    },
    {
      label: 'Create Workflow',
      value: 'create-workflow',
      onSelected: () => {
        popupManager.openForm(
          <CreateWorkflowForm
            customProcessId={teamSetupArea.id}
            processName={teamSetupArea.name}
          />,
        );
      },
    },
    {
      label: 'Copy Process',
      value: 'copy',
      onSelected: () => {
        popupManager.openForm(
          <CopyCustomProcessForm
            customProcessId={teamSetupArea.id}
            originalName={teamSetupArea.name}
          />,
        );
      },
    },
    !teamSetupArea.isDefault && {
      label: 'Make as Default',
      value: 'make-default',
      onSelected: handleMakeDefault,
    },
    {
      label: 'Archive Process',
      value: 'archive',
      onSelected: handleArchive,
    },
  ];

  return (
    <OptionSelectionButton
      text="Actions"
      size={ButtonSize.MEDIUM}
      options={options}
    />
  );
}
